import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { PaymentService, PaymentIntegration, PaymentGateway, IntegrationScope } from '../../../services/payment';
import { DropdownService, DropdownItem } from '../../../services/dropdown';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';

@Component({
  selector: 'app-payment-integrations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
  ],
  templateUrl: './payment-integrations.html',
  styleUrls: ['./payment-integrations.css'],
})
export class PaymentIntegrationsPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(PaymentService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly scopes: IntegrationScope[] = ['GLOBAL', 'SPECIFIC'];

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<PaymentIntegration[]>([]);
  gatewayOptions = signal<DropdownItem[]>([]);
  partnerOptions = signal<DropdownItem[]>([]);

  // Secret reveal toggles for the dialog.
  showApiKey = signal(false);
  showApiSecret = signal(false);
  showAuthToken = signal(false);
  showWebhook = signal(false);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.gatewayName || '').toLowerCase().includes(q) ||
        (r.gatewayCode || '').toLowerCase().includes(q) ||
        (r.merchantId || '').toLowerCase().includes(q) ||
        this.scopeLabel(r).toLowerCase().includes(q),
    );
  });

  form: FormGroup = this.fb.group({
    id: [0],
    gatewayId: [null, Validators.required],
    integrationType: ['GLOBAL' as IntegrationScope, Validators.required],
    partnerId: [null], // required only when SPECIFIC (set dynamically)
    merchantId: [null],
    apiKey: [null],
    apiSecret: [null],
    authToken: [null],
    webhookSecret: [null],
    isLive: [false],
    extraConfigJson: [null],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Scope drives the partner control.
    this.form.get('integrationType')!.valueChanges.subscribe((t) => this.applyScope(t as IntegrationScope));

    this.dropdown.partners().subscribe((o) => this.partnerOptions.set(o));
    this.loadGateways();
    this.load();
  }

  private applyScope(scope: IntegrationScope) {
    const p = this.form.get('partnerId')!;
    if (scope === 'SPECIFIC') {
      p.setValidators(Validators.required);
    } else {
      p.clearValidators();
      p.setValue(null, { emitEvent: false });
    }
    p.updateValueAndValidity({ emitEvent: false });
  }

  private loadGateways() {
    this.svc.gwGetAll().subscribe({
      next: (res: any) => {
        const data: PaymentGateway[] = res?.data ?? [];
        const opts = (Array.isArray(data) ? data : [])
          .filter((g) => g.isActive)
          .map((g) => ({ value: g.id, label: `${g.gatewayName} (${g.gatewayCode})` }));
        this.gatewayOptions.set(opts);
      },
    });
  }

  load() {
    this.loading.set(true);
    this.svc.inGetAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  // ===================== display helpers =====================
  /** Resolve a gateway label for a row even when the join is absent. */
  gatewayLabel(r: PaymentIntegration): string {
    if (r.gatewayName) return r.gatewayCode ? `${r.gatewayName} (${r.gatewayCode})` : r.gatewayName;
    const opt = this.gatewayOptions().find((o) => +o.value === r.gatewayId);
    return opt ? opt.label : `Gateway #${r.gatewayId}`;
  }

  /** "GLOBAL" or "SPECIFIC — {partnerName}". */
  scopeLabel(r: PaymentIntegration): string {
    if (r.integrationType === 'GLOBAL' || r.partnerId == null) return 'GLOBAL';
    if (r.partnerName) return `SPECIFIC — ${r.partnerName}`;
    const opt = this.partnerOptions().find((o) => +o.value === r.partnerId);
    return `SPECIFIC — ${opt ? opt.label : 'Partner #' + r.partnerId}`;
  }

  get isSpecific(): boolean {
    return this.form.get('integrationType')?.value === 'SPECIFIC';
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.resetSecrets();
    this.form.reset({
      id: 0,
      gatewayId: null,
      integrationType: 'GLOBAL',
      partnerId: null,
      merchantId: null,
      apiKey: null,
      apiSecret: null,
      authToken: null,
      webhookSecret: null,
      isLive: false,
      extraConfigJson: null,
      isActive: true,
    });
    this.applyScope('GLOBAL');
    this.showModal.set(true);
  }

  openEdit(r: PaymentIntegration) {
    this.editing.set(true);
    this.resetSecrets();
    // GetById to pull the full row (secrets are omitted from the list payload).
    this.svc.inGetById(r.id).subscribe({
      next: (res: any) => {
        const d: PaymentIntegration = res?.data ?? r;
        this.form.reset({
          id: d.id,
          gatewayId: d.gatewayId,
          integrationType: d.integrationType ?? 'GLOBAL',
          partnerId: d.partnerId ?? null,
          merchantId: d.merchantId ?? null,
          apiKey: d.apiKey ?? null,
          apiSecret: d.apiSecret ?? null,
          authToken: d.authToken ?? null,
          webhookSecret: d.webhookSecret ?? null,
          isLive: d.isLive ?? false,
          extraConfigJson: d.extraConfigJson ?? null,
          isActive: d.isActive ?? true,
        });
        this.applyScope((d.integrationType ?? 'GLOBAL') as IntegrationScope);
        this.showModal.set(true);
      },
      error: () => this.toast.error('Could not load the integration to edit.'),
    });
  }

  closeModal() {
    this.showModal.set(false);
  }

  private resetSecrets() {
    this.showApiKey.set(false);
    this.showApiSecret.set(false);
    this.showAuthToken.set(false);
    this.showWebhook.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('gatewayId')?.invalid) this.toast.warning('Gateway is required.');
      else if (this.form.get('partnerId')?.invalid) this.toast.warning('Partner is required for a SPECIFIC integration.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }

    const v = this.form.getRawValue();

    // extraConfigJson must be valid JSON if present.
    const extra = (v.extraConfigJson ?? '').toString().trim();
    if (extra && !this.isValidJson(extra)) {
      this.toast.warning('Extra Config must be valid JSON.');
      return;
    }

    const specific = v.integrationType === 'SPECIFIC';
    const dto: Partial<PaymentIntegration> = {
      id: this.editing() ? Number(v.id) : 0,
      gatewayId: Number(v.gatewayId),
      integrationType: v.integrationType,
      // GLOBAL → never send a partner; SPECIFIC → required partner.
      partnerId: specific ? Number(v.partnerId) : null,
      merchantId: this.blankToNull(v.merchantId),
      apiKey: this.blankToNull(v.apiKey),
      apiSecret: this.blankToNull(v.apiSecret),
      authToken: this.blankToNull(v.authToken),
      webhookSecret: this.blankToNull(v.webhookSecret),
      isLive: !!v.isLive,
      extraConfigJson: extra || null,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.inSave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save integration.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Integration updated' : 'Integration created'));
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== inline active toggle =====================
  toggleActive(r: PaymentIntegration) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.inSetActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<PaymentIntegration>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  // ===================== delete =====================
  async remove(r: PaymentIntegration) {
    const ok = await this.toast.confirm(
      `The ${this.scopeLabel(r)} integration for ${this.gatewayLabel(r)} will be deleted.`,
      'Delete integration?',
      'Delete',
    );
    if (!ok) return;
    this.svc.inDelete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Integration deleted');
        this.load();
      },
    });
  }

  // ===================== utils =====================
  private blankToNull(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }

  private isValidJson(s: string): boolean {
    try {
      JSON.parse(s);
      return true;
    } catch {
      return false;
    }
  }
}
